import {XMarkIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import Image from 'next/image';
import React, {ReactElement, useMemo} from 'react';
import {Product} from 'src/features/query/graphql';

import {PhotoIcon} from '../../../../resources/Icons';
import URLS from '../../../../urls';
import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {SupplierBasic, SupplierTag} from '../../marketplace/types';
import Button from '../buttons/Button';
import SquareButton from '../buttons/SquareButton';
import Modal from '../Modal';
import Tags from '../Tags';

type Props = {
  product?: Product;
  supplier?: SupplierBasic;
  onRequestClose: () => void;
};

const ProductDetailModal = ({product, supplier, onRequestClose}: Props): ReactElement => {
  const tags = useMemo(() => {
    const tags: SupplierTag[] = [];
    if (product?.serviceType) {
      tags.push({tag: product.serviceType.name, type: 'service'});
    }
    product?.cropTypes.forEach(crop => {
      tags.push({tag: crop.name, type: 'crop'});
    });
    return tags;
  }, [product?.cropTypes, product?.serviceType]);

  return (
    <Modal isOpen={!!product} title={'Product detail'} className={'relative !grow-0'}>
      <SquareButton
        className={'!absolute top-xs right-xs border-primary-800'}
        variant={'secondary'}
        onClick={onRequestClose}
      >
        <XMarkIcon className={'w-sm'} />
      </SquareButton>

      <Col>
        <Row>
          <Col
            className={
              'relative flex-none min-h-[33rem] w-[33.6rem] max-w-[38%] bg-gray-100 items-center justify-center'
            }
          >
            {!product?.image && <PhotoIcon className={'w-[5rem] h-[5rem]'} />}
            {product?.image && <Image src={product.image.url} alt={product.name} fill className={'object-cover'} />}
          </Col>
          <Col className={'px-lg py-md gap-xs justify-center'}>
            {!!supplier && <p className={'font-semibold text-sm text-primary-600'}>{supplier.name}</p>}
            <h1 className="font-semibold text-3xl leading-none">{product?.name}</h1>
            <p className="text-sm whitespace-pre-line break-words">{product?.description}</p>
            {!!supplier && (
              <Col className={'gap-2'}>
                <h2 className={'text-base font-semibold'}>Location</h2>
                <p className="text-sm">
                  {supplier.address} · {supplier.town}
                </p>
              </Col>
            )}
            {tags.length > 0 && <Tags tags={tags} expanded />}
          </Col>
        </Row>
        {(supplier || !!product?.external_Link) && (
          <Row
            className={classNames(
              'px-lg mobile:px-sm py-xs border-t border-gray-100',
              supplier ? 'justify-between' : 'justify-end'
            )}
          >
            {supplier && (
              <Button variant={'secondary'} href={URLS.marketPlace.supplier(supplier.slug).about}>
                Contact Supplier
              </Button>
            )}
            {!!product?.external_Link && (
              <Button
                variant={'primary'}
                href={product.external_Link}
                target="_blank"
                rel="noopener noreferrer"
                accessory={'forward'}
              >
                View in supplier site
              </Button>
            )}
          </Row>
        )}
      </Col>
    </Modal>
  );
};

export default ProductDetailModal;
